import styled, { css, keyframes } from "styled-components";
import { Link } from "react-router-dom";
import {
  glassCardStyles,
  gradientTextStyles,
  theme,
  typography,
} from "../Homepage/styles";

const linkReset = css`
  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
    color: inherit;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const CmsShell = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow: hidden;
  background: ${theme.bg};
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
`;

export const CmsGlow = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background-image: ${theme.gradientHero};

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse at 80% 0%,
      hsl(var(--hp-primary) / 0.12),
      transparent 60%
    );
  }
`;

export const CmsMain = styled.main`
  position: relative;
  z-index: 10;
  flex: 1;
  width: 100%;
  max-width: 72rem;
  margin: 0 auto;
  padding: 112px 16px 80px;

  @media (min-width: 640px) {
    padding: 120px 16px 96px;
  }
`;

export const CmsMasthead = styled.header`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
`;

export const CmsEyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border: 1px solid hsl(var(--hp-primary) / 0.3);
  border-radius: 9999px;
  background: hsl(var(--hp-primary) / 0.1);
  color: ${theme.primary};
  font-size: 0.75rem;
  font-weight: ${typography.weights.semibold};
  letter-spacing: 0.08em;
  text-transform: uppercase;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.primary};
  }
`;

export const CmsTitle = styled.h1`
  margin: 16px 0 0;
  color: ${theme.foreground};
  font-size: 2.25rem;
  font-weight: ${typography.weights.bold};
  letter-spacing: -0.02em;
  line-height: 1.15;

  @media (min-width: 768px) {
    font-size: 3rem;
  }

  span {
    ${gradientTextStyles}
  }
`;

export const CmsLead = styled.p`
  margin: 12px 0 0;
  max-width: 36rem;
  color: ${theme.mutedForeground};
  font-size: 1rem;
  line-height: 1.55;
`;

export const CmsHeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

export const CmsRoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 2.25rem;
  padding: 0 12px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 9999px;
  color: ${theme.foreground};
  font-size: 0.8125rem;
  font-weight: ${typography.weights.medium};
  text-transform: capitalize;
`;

export const CmsPrimaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 2.75rem;
  padding: 0 22px;
  border: none;
  border-radius: 9999px;
  background-image: ${theme.gradientGold};
  box-shadow: ${theme.shadowButton};
  color: ${theme.primaryForeground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.bold};
  transition: transform 0.15s ease;
  ${linkReset}

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    transform: scale(1.02);
  }
`;

export const CmsStats = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-top: 32px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

export const CmsStatCard = styled.div`
  ${glassCardStyles};
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border-radius: 1rem;
  animation: ${fadeIn} 0.4s ease both;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

export const CmsStatIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  background: hsl(var(--hp-primary) / 0.12);
  color: ${theme.primary};

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const CmsStatValue = styled.p`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.5rem;
  font-weight: ${typography.weights.bold};
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

export const CmsStatLabel = styled.p`
  margin: 4px 0 0;
  color: ${theme.mutedForeground};
  font-size: 0.75rem;
  font-weight: ${typography.weights.medium};
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const CmsStatHint = styled.p`
  margin: 4px 0 0;
  color: hsl(var(--hp-muted-foreground) / 0.75);
  font-size: 0.6875rem;
  font-weight: ${typography.weights.normal};
  letter-spacing: 0.01em;
  line-height: 1.3;
`;

export const CmsToolbarCard = styled.section`
  ${glassCardStyles};
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
  padding: 16px;
  border-radius: 1rem;

  @media (min-width: 640px) {
    padding: 20px;
  }
`;

export const CmsListCard = styled.section`
  ${glassCardStyles};
  margin-top: 24px;
  border-radius: 1rem;
  overflow: hidden;
`;

export const CmsSearchRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: center;
  }
`;

export const CmsSearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;

  svg {
    position: absolute;
    left: 14px;
    top: 50%;
    width: 16px;
    height: 16px;
    transform: translateY(-50%);
    color: ${theme.mutedForeground};
    pointer-events: none;
  }
`;

export const CmsSearchInput = styled.input`
  width: 100%;
  min-height: 2.75rem;
  padding: 0 14px 0 40px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 0.75rem;
  background: hsl(var(--hp-background) / 0.4);
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 0.9375rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px hsl(var(--hp-primary) / 0.15);
  }

  &::placeholder {
    color: ${theme.mutedForeground};
  }
`;

export const CmsStatusSeg = styled.div`
  display: inline-flex;
  align-self: flex-start;
  padding: 4px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 0.75rem;
  background: hsl(var(--hp-background) / 0.4);
`;

export const CmsStatusSegBtn = styled.button`
  min-height: 2.25rem;
  padding: 0 16px;
  border: none;
  border-radius: 0.5rem;
  background: ${({ $active }) =>
    $active ? "hsl(var(--hp-primary) / 0.15)" : "transparent"};
  box-shadow: ${({ $active }) =>
    $active ? "inset 0 0 0 1px hsl(var(--hp-primary) / 0.3)" : "none"};
  color: ${({ $active }) =>
    $active ? theme.primary : theme.mutedForeground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: ${({ $active }) =>
      $active ? theme.primary : theme.foreground};
  }
`;

export const CmsClearBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  min-height: 2.5rem;
  padding: 0 12px;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: ${theme.mutedForeground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${theme.foreground};
  }
`;

export const CmsPublisherRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid hsl(var(--hp-border) / 0.5);
`;

export const CmsPublisherLabel = styled.span`
  margin-right: 4px;
  color: ${theme.mutedForeground};
  font-size: 0.75rem;
  font-weight: ${typography.weights.semibold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const CmsPublisherBtn = styled.button`
  width: 40px;
  height: 40px;
  padding: 2px;
  border: 1px solid
    ${({ $active }) =>
      $active ? theme.primary : "hsl(var(--hp-border) / 0.6)"};
  border-radius: 9999px;
  background: ${({ $all }) =>
    $all ? "hsl(var(--hp-secondary) / 0.6)" : "transparent"};
  cursor: pointer;
  opacity: ${({ $active }) => ($active ? 1 : 0.85)};
  box-shadow: ${({ $active }) =>
    $active ? `0 0 0 2px ${theme.bg}, 0 0 0 4px ${theme.primary}` : "none"};
  transition: border-color 0.15s ease, opacity 0.15s ease, box-shadow 0.15s ease;

  img {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 9999px;
    object-fit: cover;
  }

  &:hover {
    border-color: hsl(var(--hp-primary) / 0.5);
    opacity: 1;
  }
`;

export const CmsPublisherAll = styled(CmsPublisherBtn)`
  font-size: 0.625rem;
  font-weight: ${typography.weights.semibold};
  color: ${theme.foreground};
`;

export const CmsListHead = styled.div`
  display: none;
  grid-template-columns: ${({ $admin }) =>
    $admin
      ? "4.5rem minmax(0, 4fr) 1.6fr 1.1fr 1.4fr 1.8fr 1fr"
      : "4.5rem minmax(0, 5fr) 1.1fr 1.4fr 1.8fr 1fr"};
  gap: 16px;
  padding: 14px 24px;
  border-bottom: 1px solid hsl(var(--hp-border) / 0.5);
  background: hsl(var(--hp-background) / 0.2);
  color: ${theme.mutedForeground};
  font-size: 0.6875rem;
  font-weight: ${typography.weights.semibold};
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media (min-width: 768px) {
    display: grid;
  }

  span:last-child {
    text-align: right;
  }
`;

export const CmsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const CmsRow = styled.li`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  padding: 16px 24px;
  border-bottom: 1px solid hsl(var(--hp-border) / 0.5);
  animation: ${fadeIn} 0.35s ease both;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: hsl(var(--hp-primary) / 0.04);
  }

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 2px;
    height: 100%;
    background-image: ${theme.gradientGold};
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover::before {
    opacity: 1;
  }

  @media (min-width: 768px) {
    grid-template-columns: ${({ $admin }) =>
      $admin
        ? "4.5rem minmax(0, 4fr) 1.6fr 1.1fr 1.4fr 1.8fr 1fr"
        : "4.5rem minmax(0, 5fr) 1.1fr 1.4fr 1.8fr 1fr"};
    gap: 16px;
    align-items: center;
  }
`;

export const CmsIdCell = styled.span`
  color: ${theme.mutedForeground};
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
`;

export const CmsTitleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const CmsAvatar = styled.img`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 9999px;
  object-fit: cover;
`;

export const CmsTitleLink = styled(Link)`
  min-width: 0;
  color: ${theme.foreground};
  font-weight: ${typography.weights.medium};
  line-height: 1.35;
  ${linkReset}

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  &:hover {
    color: ${theme.primary};
  }
`;

export const CmsPublisherCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;

  img {
    width: 28px;
    height: 28px;
    border: 1px solid hsl(var(--hp-border) / 0.6);
    border-radius: 9999px;
    object-fit: cover;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const CmsStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid
    ${({ $published }) =>
      $published
        ? "hsl(var(--hp-primary) / 0.4)"
        : "hsl(var(--hp-border) / 0.6)"};
  border-radius: 9999px;
  background: ${({ $published }) =>
    $published ? "hsl(var(--hp-primary) / 0.1)" : "hsl(var(--hp-muted) / 0.4)"};
  color: ${({ $published }) =>
    $published ? theme.primary : theme.mutedForeground};
  font-size: 0.75rem;
  font-weight: ${typography.weights.medium};
  white-space: nowrap;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $published }) =>
      $published ? theme.primary : "hsl(var(--hp-muted-foreground) / 0.6)"};
  }
`;

export const CmsViewsCell = styled.div`
  color: ${theme.foreground};
  font-size: 0.875rem;
  font-variant-numeric: tabular-nums;

  > .mdOnly {
    color: ${theme.mutedForeground};

    @media (min-width: 768px) {
      display: none;
    }
  }
`;

export const CmsDateCell = styled.div`
  color: ${theme.mutedForeground};
  font-size: 0.875rem;

  > .mdOnly {
    @media (min-width: 768px) {
      display: none;
    }
  }

  em {
    display: none;
    font-style: normal;
    color: hsl(var(--hp-muted-foreground) / 0.6);

    @media (min-width: 1024px) {
      display: inline;
    }
  }
`;

export const CmsActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;

  @media (min-width: 768px) {
    justify-content: flex-end;
    opacity: 0;
    transition: opacity 0.15s ease;

    ${CmsRow}:hover &,
    ${CmsRow}:focus-within & {
      opacity: 1;
    }
  }
`;

export const CmsIconLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  color: ${theme.foreground};
  ${linkReset}

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: hsl(var(--hp-primary) / 0.1);
    color: ${theme.primary};
  }
`;

export const CmsIconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 9999px;
  background: transparent;
  color: hsl(0 72% 51%);
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: hsl(0 72% 51% / 0.1);
  }
`;

export const CmsEmpty = styled.div`
  padding: 64px 24px;
  text-align: center;
  color: ${theme.mutedForeground};
`;

export const CmsEmptyIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 1rem;
  background: hsl(var(--hp-primary) / 0.1);
  color: ${theme.primary};

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const CmsEmptyTitle = styled.p`
  margin: 16px 0 0;
  color: ${theme.foreground};
  font-size: 1.125rem;
  font-weight: ${typography.weights.semibold};
`;

export const CmsEmptyText = styled.p`
  margin: 8px 0 0;
  font-size: 0.875rem;
`;

export const CmsLoading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 72px 24px;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;

  svg {
    width: 24px;
    height: 24px;
    color: ${theme.primary};
    animation: ${spin} 0.8s linear infinite;
  }
`;

export const CmsSentinel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  margin-top: 16px;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;

  svg {
    width: 16px;
    height: 16px;
    color: ${theme.primary};
    animation: ${spin} 0.8s linear infinite;
  }
`;

export const CmsRestricted = styled.div`
  ${glassCardStyles};
  max-width: 28rem;
  margin: 48px auto 0;
  padding: 40px 32px;
  border-radius: 1.5rem;
  text-align: center;
  box-shadow: ${theme.shadowCard};
`;

export const CmsRestrictedIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 1rem;
  background: hsl(var(--hp-primary) / 0.15);
  color: ${theme.primary};

  svg {
    width: 28px;
    height: 28px;
  }
`;

export const CmsRestrictedTitle = styled.h1`
  margin: 20px 0 0;
  font-size: 1.5rem;
  font-weight: ${typography.weights.bold};
`;

export const CmsRestrictedText = styled.p`
  margin: 8px 0 0;
  color: ${theme.mutedForeground};
  font-size: 0.9375rem;
  line-height: 1.5;
`;

export const CmsRestrictedActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
`;

export const CmsOutlineLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0 16px;
  border: 1px solid hsl(var(--hp-border) / 0.8);
  border-radius: 9999px;
  color: ${theme.foreground};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  ${linkReset}

  &:hover {
    background: hsl(var(--hp-muted) / 0.5);
  }
`;

export const CmsOutlineBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.5rem;
  margin-top: 20px;
  padding: 0 16px;
  border: 1px solid hsl(var(--hp-border) / 0.8);
  border-radius: 9999px;
  background: transparent;
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  cursor: pointer;

  &:hover {
    background: hsl(var(--hp-muted) / 0.5);
  }
`;

export const CmsDeleteBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: hsl(0 0% 0% / 0.6);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

export const CmsDeleteDialog = styled.div`
  ${glassCardStyles};
  width: 100%;
  max-width: 28rem;
  padding: 24px;
  border-radius: 1rem;
  box-shadow: 0 24px 60px hsl(0 0% 0% / 0.45);
`;

export const CmsDeleteTitle = styled.h2`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.125rem;
  font-weight: ${typography.weights.semibold};
  letter-spacing: -0.01em;
  line-height: 1.35;
`;

export const CmsDeleteCopy = styled.p`
  margin: 10px 0 0;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
  line-height: 1.55;

  strong {
    color: ${theme.foreground};
    font-weight: ${typography.weights.medium};
  }
`;

export const CmsDeleteActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
`;

export const CmsDeleteCancel = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0 16px;
  border: 1px solid hsl(var(--hp-border) / 0.9);
  border-radius: 0.5rem;
  background: transparent;
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: hsl(var(--hp-muted) / 0.5);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const CmsDeleteConfirm = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0 16px;
  border: none;
  border-radius: 0.5rem;
  background: hsl(0 72% 51%);
  color: #fff;
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: hsl(0 72% 45%);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;
